import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  formBuilderApi,
  templateConfigKeys,
  type FormListItem,
} from '@/features/quote-and-apply-builder';

export function useCloneTemplates(forms: FormListItem[] | undefined) {
  const queryClient = useQueryClient();
  const [sourceForm, setSourceForm] = useState<FormListItem | null>(null);
  const [destinationFormId, setDestinationFormId] = useState('');

  const destinationForms = useMemo(() => {
    if (!sourceForm || !forms) return [];
    return forms.filter(
      form =>
        form.id !== sourceForm.id &&
        form.product === sourceForm.product &&
        form.formType === sourceForm.formType,
    );
  }, [forms, sourceForm]);

  const openCloneDialog = useCallback((event: React.MouseEvent, form: FormListItem) => {
    event.stopPropagation();
    setSourceForm(form);
    setDestinationFormId('');
  }, []);

  const closeCloneDialog = useCallback(() => {
    setSourceForm(null);
    setDestinationFormId('');
  }, []);

  const { mutate: cloneTemplates, isPending: isCloning } = useMutation({
    mutationFn: async () => {
      if (!sourceForm || !destinationFormId) {
        throw new Error('Select a destination form');
      }

      const [sourceResult, destinationResult] = await Promise.all([
        formBuilderApi.getTemplateConfigs(sourceForm.id),
        formBuilderApi.getTemplateConfigs(destinationFormId),
      ]);

      if (sourceResult.configs.length === 0) {
        throw new Error('The source form has no templates to clone.');
      }

      if (destinationResult.configs.length > 0) {
        throw new Error(
          'The destination form already has templates. Cloning is only supported into an empty form.',
        );
      }

      return formBuilderApi.createTemplateConfigs({
        insuranceCarrierProductPolicyApplicationFormId: destinationFormId,
        templates: sourceResult.configs.map(config => ({
          templateName: config.templateName,
          defaultSequence: config.defaultSequence,
          isRequired: config.isRequired,
          allowedRoles: config.allowedRoles,
          conditional: config.conditional,
          metadata: config.metadata,
          status: config.status,
        })),
      });
    },
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: templateConfigKeys.detail(destinationFormId) });
      const count = result.configs.length;
      toast.success(`Cloned ${count} template${count === 1 ? '' : 's'} to the destination form.`);
      closeCloneDialog();
    },
    onError: err => {
      toast.error(err instanceof Error ? err.message : 'Failed to clone templates');
    },
  });

  return {
    sourceForm,
    destinationFormId,
    setDestinationFormId,
    destinationForms,
    isCloning,
    isCloneDialogOpen: !!sourceForm,
    openCloneDialog,
    closeCloneDialog,
    cloneTemplates,
  };
}
